import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { buildConsoleRequestHeaders } from '../utils/backendRequest';

const OrdersContext = createContext();

function ordersRequestHeaders(token, user) {
  return buildConsoleRequestHeaders(token, user);
}

function deliveryCoreBaseUrl() {
  return process.env.REACT_APP_DELIVERY_CORE_API_URL || '';
}

export function OrdersProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const refreshInFlightRef = useRef(false);
  const ordersRef = useRef(orders);

  ordersRef.current = orders;

  const refreshOrders = useCallback(async ({ fromDate, toDate } = {}) => {
    if (refreshInFlightRef.current) {
      return ordersRef.current;
    }

    refreshInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setOrders([]);
      setError('ordersMissingToken');
      refreshInFlightRef.current = false;
      return [];
    }

    if (!fromDate || !toDate) {
      setOrders([]);
      setError('ordersMissingDateRange');
      refreshInFlightRef.current = false;
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const url = new URL(`${deliveryCoreBaseUrl()}/v1/calculation-payloads`, window.location.origin);
      url.searchParams.set('from_date', fromDate);
      url.searchParams.set('to_date', toDate);

      const res = await fetch(url.toString(), {
        headers: ordersRequestHeaders(token, user),
      });

      if (res.ok) {
        const data = await res.json();
        const list = (Array.isArray(data) ? data : []).map((row) => ({
          id: row?.id,
          order_number: row?.order_number,
          customer_id: row?.customer_id,
          environment: row?.environment,
          language: row?.language,
          created_at: row?.created_at,
          confirmed_by_customer: row?.confirmed_by_customer,
          confirmed_at: row?.confirmed_at,
          delivery_cost:
            row?.delivery_cost ??
            row?.deliveryCost ??
            row?.selected_rate?.customer_amount ??
            null,
          items_count: Array.isArray(row?.items) ? row.items.length : 0,
          raw: row,
        }));
        setOrders(list);
        return list;
      }

      setOrders([]);
      setError('ordersLoadFailed');
      return [];
    } catch (err) {
      console.error('Failed to load orders:', err);
      setOrders([]);
      setError('ordersLoadFailed');
      return [];
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, [user]);

  const createCalculationPayload = useCallback(async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('ordersMissingToken');
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      const url = new URL(`${deliveryCoreBaseUrl()}/v1/calculation-payloads`, window.location.origin);
      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...ordersRequestHeaders(token, user),
        },
        body: JSON.stringify(payload || {}),
      });

      if (!res.ok) {
        setError('ordersCreateFailed');
        return null;
      }

      const row = await res.json();
      const created = {
        id: row?.id,
        order_number: row?.order_number,
        customer_id: row?.customer_id,
        environment: row?.environment,
        language: row?.language,
        created_at: row?.created_at,
        confirmed_by_customer: row?.confirmed_by_customer,
        confirmed_at: row?.confirmed_at,
        items_count: Array.isArray(row?.items) ? row.items.length : 0,
        raw: row,
      };
      setOrders((prev) => [created, ...(Array.isArray(prev) ? prev : [])]);
      return created;
    } catch (err) {
      console.error('Failed to create calculation payload:', err);
      setError('ordersCreateFailed');
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const deleteCalculationPayload = useCallback(async (payloadId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return { ok: false, error: 'ordersMissingToken' };
    }

    const id = String(payloadId || '').trim();
    if (!id) {
      return { ok: false, error: 'ordersDeleteFailed' };
    }

    try {
      const url = new URL(
        `${deliveryCoreBaseUrl()}/v1/calculation/${encodeURIComponent(id)}`,
        window.location.origin
      );
      const res = await fetch(url.toString(), {
        method: 'DELETE',
        headers: ordersRequestHeaders(token, user),
      });

      if (res.status === 204) {
        setOrders((prev) =>
          (Array.isArray(prev) ? prev : []).filter((row) => String(row?.id) !== id)
        );
        return { ok: true };
      }

      if (res.status === 409) {
        let detail = '';
        try {
          const body = await res.json();
          detail = String(body?.detail || '');
        } catch {
          detail = '';
        }
        if (/shipment/i.test(detail)) {
          return { ok: false, error: 'ordersDeleteHasShipments' };
        }
        return { ok: false, error: 'ordersDeleteConfirmed' };
      }

      return { ok: false, error: 'ordersDeleteFailed' };
    } catch (err) {
      console.error('Failed to delete calculation payload:', err);
      return { ok: false, error: 'ordersDeleteFailed' };
    }
  }, [user]);

  return (
    <OrdersContext.Provider
      value={{
        orders,
        refreshOrders,
        createCalculationPayload,
        deleteCalculationPayload,
        loading,
        error,
        setError,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
}

export function useOrders() {
  return useContext(OrdersContext);
}
